/**
 * Service Lines Custom Slide
 * Displays category cards that link to detailed service line information
 */

import React, { useState } from 'react';
import { BottomNavigation } from '../../../components/shared/Navigation';
import { NavigationProps } from '../../../types/slideTypes';
import { serviceCategories, ServiceCategory } from '../serviceLines';
import { ServiceLineDetail } from './ServiceLineDetail';

interface ServiceLinesSlideProps extends NavigationProps {}

export const ServiceLinesSlide: React.FC<ServiceLinesSlideProps> = ({
  currentSlide,
  totalSlides,
  onPrevious,
  onNext,
  onGoToSlide,
}) => {
  const [selectedCategory, setSelectedCategory] = useState<ServiceCategory | null>(null);

  // If a category is selected, show detail view
  if (selectedCategory) {
    return (
      <ServiceLineDetail
        category={selectedCategory}
        onBack={() => setSelectedCategory(null)}
        currentSlide={currentSlide}
        totalSlides={totalSlides}
        onPrevious={onPrevious}
        onNext={onNext}
        onGoToSlide={onGoToSlide}
      />
    );
  }

  // Main view: Category cards
  return (
    <div className="h-full bg-white flex flex-col relative">
      {/* Main Content */}
      <div className="flex-1 p-4 pt-8 md:p-6 md:pt-10 lg:p-8 lg:pt-12 overflow-y-auto">
        <div className="max-w-7xl mx-auto">
          {/* Title and Progress Bar */}
          <div className="mb-4 md:mb-6">
            <div className="mb-3 md:mb-4">
              <h1
                className="font-bold text-black"
                style={{ fontSize: 'clamp(1.75rem, 4vw, 2.25rem)' }}
              >
                Service Lines
              </h1>
              <p className="text-sm text-slate-600 mt-2 max-w-4xl leading-relaxed">
                These service categories represent our core capabilities. We maintain a flexible,
                vendor-agnostic approach and regularly staff emerging technologies and specialized
                solutions to meet evolving client needs.
              </p>
            </div>
            <div className="w-full h-1 rounded" style={{ backgroundColor: '#1e293b' }}></div>
          </div>

          {/* Category Cards Grid - Unified Slate Color Scheme */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {serviceCategories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category)}
                className="bg-slate-50 border-2 border-slate-200 hover:border-slate-400 rounded-xl p-4 md:p-5 text-left transition-all duration-200 hover:shadow-xl hover:scale-105 transform flex flex-col min-h-[180px]"
              >
                {/* Icon Only (No Service Count) */}
                <div className="flex items-start justify-between mb-2">
                  <div className="text-3xl bg-slate-100 rounded-lg p-2">
                    {category.icon}
                  </div>
                </div>

                {/* Category Name */}
                <h3 className="text-lg font-bold text-slate-700 mb-2">
                  {category.name}
                </h3>

                {/* Service Line Preview - Flex Grow to Push View Details Down */}
                <div className="space-y-1 flex-grow">
                  {category.serviceLines.slice(0, 3).map((service, idx) => (
                    <p key={idx} className="text-sm text-slate-600 truncate">
                      • {service.name}
                    </p>
                  ))}
                  {category.serviceLines.length > 3 && (
                    <p className="text-sm text-slate-500 italic">
                      + {category.serviceLines.length - 3} more
                    </p>
                  )}
                </div>

                {/* Click prompt - Always at Bottom */}
                <div className="mt-3 pt-3 border-t border-slate-200 flex items-center justify-between">
                  <span className="text-xs font-medium text-slate-700">
                    View Details
                  </span>
                  <svg
                    className="w-4 h-4 text-slate-700"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Navigation */}
      <BottomNavigation
        currentSlide={currentSlide}
        totalSlides={totalSlides}
        onPrevious={onPrevious}
        onNext={onNext}
        onGoToSlide={onGoToSlide}
      />
    </div>
  );
};
